import clsx from 'clsx'
import { useMemo } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import type { TextProps } from '@wartek-id/text-v1'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import { useAPIGetPredikatKinerjaList } from 'api/kinerja/useAPIKinerja'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import {
  TableBody,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { getSatuanPendidikanByNpsn } from 'app/KinerjaShared/utils/profile'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'

import type { TPredicateRecommendationResult } from 'types/kinerja-pelaksanaan'

interface SectionPanduanPenetapanPredikatKinerjaProps {
  actionNode?: React.ReactNode
  className?: string
  npsn?: string
  onPanduanClick?: () => void
}

export function SectionPanduanPenetapanPredikatKinerja({
  actionNode,
  className,
  npsn,
  onPanduanClick,
}: SectionPanduanPenetapanPredikatKinerjaProps) {
  const { profile, isSuccess } = useRoleContext()
  const { isDemoMode } = useDemoContext()

  const { isLoading, data } = useAPIGetPredikatKinerjaList({
    params: { npsn },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
    isDemoMode,
  })

  const isPredikatAvailable =
    data?.data?.predicateOrganization?.deliveryStatus === 'SENT'

  const tableResultMinimum: {
    label: string
    value: TPredicateRecommendationResult
    className?: string
    childrenClassName?: string
    textColor?: TextProps['color']
  }[] = useMemo(() => {
    if (isLoading) return []

    const selectedPredicateClasses = {
      className: 'bg-cyan-50',
      textColor: 'inverse',
      childrenClassName: 'bg-blue-20',
    }
    const defaultTable: {
      label: string
      value: TPredicateRecommendationResult
    }[] = [
      {
        label: 'Istimewa',
        value: 'VERY_GOOD',
      },
      {
        label: 'Baik',
        value: 'GOOD',
      },
      {
        label: 'Butuh Perbaikan',
        value: 'NEED_IMPROVEMENT',
      },
      {
        label: 'Kurang',
        value: 'BAD',
      },
      {
        label: 'Sangat Kurang',
        value: 'VERY_BAD',
      },
    ]
    return defaultTable.map((thisTable) =>
      thisTable.value === data?.data?.predicateOrganization?.value &&
      isPredikatAvailable
        ? { ...thisTable, ...selectedPredicateClasses }
        : thisTable
    )
  }, [data, isLoading, isPredikatAvailable])

  return (
    <section className={clsx('space-y-6', className)}>
      <div className="max-w-4xl space-y-4">
        <Text variant="heading-md" as="h2" className="mb-6">
          Pengelolaan Kinerja
          <br />
          {getSatuanPendidikanByNpsn(profile, npsn, isDemoMode)}
          <br />
          <CardPeriodType withYear />
        </Text>

        {(isLoading || isPredikatAvailable) && (
          <div className="space-y-4">
            <Text color="subdued">
              Berikut Predikat Kinerja Organisasi Anda:
            </Text>
            <div className="flex border border-interactive-default rounded justify-between items-center">
              <div className="px-4 py-6">
                <Text variant="action">
                  Predikat Kinerja Organisasi{' '}
                  {getSatuanPendidikanByNpsn(profile, npsn, isDemoMode)}
                </Text>
                <Text variant="body-sm" color="subdued">
                  Berdasarkan rekomendasi dari Kemendikbudristek bersama Dinas
                  Pendidikan.
                </Text>
              </div>
              <div className="bg-cyan-50 self-stretch items-center flex px-4 w-48">
                <Text variant="heading-md" color="inverse">
                  {isLoading ? (
                    <Icon color="inherit" className="motion-safe:animate-spin">
                      hourglass_top
                    </Icon>
                  ) : (
                    ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[
                      data?.data?.predicateOrganization?.value
                    ]
                  )}
                </Text>
              </div>
            </div>
          </div>
        )}

        <TablePelaksanaanKinerja>
          <thead className="bg-gray-5 !text-left border-b border-gray-20">
            <TableRow>
              <TableHeaderItem
                rowSpan={2}
                className="text-center align-middle border"
              >
                Predikat Kinerja
                <br />
                Organisasi
              </TableHeaderItem>
              <TableHeaderItem colSpan={5} className="text-center border">
                Predikat Kinerja Pegawai
              </TableHeaderItem>
            </TableRow>
            <TableRow>
              <TableHeaderItem className="border-l bg-white">
                Sangat Baik
              </TableHeaderItem>
              <TableHeaderItem className="bg-white">Baik</TableHeaderItem>
              <TableHeaderItem className="bg-white">
                Butuh Perbaikan
              </TableHeaderItem>
              <TableHeaderItem className="bg-white">Kurang</TableHeaderItem>
              <TableHeaderItem className="border-r bg-white">
                Sangat Kurang
              </TableHeaderItem>
            </TableRow>
          </thead>
          <TableBody>
            {isLoading && <TableShimmer columns={6} rows={5} />}
            {tableResultMinimum?.map((thisResult, index) => (
              <TableRow key={`tabel_minimum_${thisResult.label}`}>
                <TableRowItem
                  className={clsx(
                    'border text-center py-2',
                    thisResult?.className
                  )}
                >
                  <Text variant="action" color={thisResult?.textColor}>
                    {thisResult.label}
                  </Text>
                </TableRowItem>
                {isSuccess &&
                  tableResultMinimum.map((_, indexChild) => (
                    <TableRowItem
                      className={clsx(
                        'last:border-r py-2',
                        indexChild === 0 && 'border-l',
                        thisResult?.childrenClassName
                      )}
                      key={`child_${thisResult.label}_${index}_${indexChild}`}
                    >
                      <Text variant="body-sm">
                        {indexChild === index ? 'Min. 50%' : '-'}
                      </Text>
                    </TableRowItem>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </TablePelaksanaanKinerja>

        <div className="flex p-4 space-x-6 justify-between items-center rounded bg-cyan-5">
          <Text className="max-w-2xl" variant="body-sm">
            Panduan di atas <strong>hanya</strong> berlaku untuk satuan
            pendidikan dan merupakan kesepakatan antara Kemendikbudristek dengan
            KemenPANRB sebagai adaptasi PermenPANRB No. 6 Tahun 2022.
          </Text>

          <Button
            as="a"
            className="whitespace-nowrap !text-gray-90"
            color="white"
            href={`${process.env.NEXT_PUBLIC_STATIC_GCS_CDN}/pengelolaan-kinerja/Permen%20PANRB%20No.%206%20Tahun%202022.pdf`}
            size="md"
            target="blank"
            type={undefined}
            variant="ghost"
            onClick={onPanduanClick}
          >
            Baca peraturan
          </Button>
        </div>
      </div>

      <hr />

      <div className="flex justify-end">{actionNode}</div>
    </section>
  )
}
